import { InlineTranslateable } from '@fmnts/i18n';
import * as Rec from 'effect/Record';
import { ConditionDefinition } from '../condition/conditions';
import { ValidatorConfig } from '../validation/validation.types';
import { FormDeclarationNode } from './form-declaration-tree';
import {
  ActionDefinition,
  FieldDefinition,
  FieldScope,
  InputDefinition,
  SelectInput,
  SelectInputItem,
  TemplateDefinition,
  TextInput,
} from './form.types';

// ==================================================================
// Utility Types for different fields
// ==================================================================

type FormBaseFields = Omit<FieldDefinition, 'widgetType' | 'widgetOptions'>;

export type FormFieldWidget =
  InputDefinition | TemplateDefinition | ActionDefinition;

export type WidgetType = 'input' | 'textbox' | 'action' | 'unknown';

export type FormInputField = FieldDeclaration<'input', InputFieldDeclaration>;
export type FormSelectField = FieldDeclaration<
  'input',
  SelectInputFieldDeclaration
>;
export type FormTextInputField = FieldDeclaration<
  'input',
  TextInputFieldDeclaration
>;

export type FormTextboxField = FieldDeclaration<
  'textbox',
  TextboxFieldDeclaration
>;

export type FormActionField = FieldDeclaration<
  'action',
  ActionFieldDeclaration
>;

// ==================================================================
// Base classes for Fields, Fieldsets & Pages
// ==================================================================

/**
 * Class for a Field
 *
 * This class uses the builder-pattern to compose different types
 * of fields.
 */
export class FieldDeclaration<
  TWidgetType extends WidgetType = WidgetType,
  T extends FormFieldWidget = FormFieldWidget,
> implements FieldDefinition {
  readonly key: string;
  readonly attrs?: Rec.ReadonlyRecord<string, any>;
  readonly condition?: ConditionDefinition | undefined;
  readonly label: InlineTranslateable | undefined;
  readonly widget: string | undefined;
  readonly style?: string | undefined;

  readonly widgetOptions: Readonly<T>;
  readonly widgetType: TWidgetType | 'unknown';

  constructor(
    dto: Readonly<FormBaseFields>,
    fieldOptions: T,
    fieldType: TWidgetType | 'unknown' = 'unknown',
  ) {
    this.key = dto.key;
    this.attrs = dto.attrs;
    this.label = dto.label;
    this.widget = dto.widget;
    this.style = dto.style;
    this.condition = dto.condition;

    this.widgetOptions = fieldOptions;
    this.widgetType = fieldType;
  }
}

// ==================================================================
// Input Fields
// ==================================================================

/**
 * Basic Field Input
 *
 * Base Implementation, needed for Declarative-Forms to setup
 * Input Fields.
 */
export interface InputFieldDeclaration extends InputDefinition {
  readonly defaultValue?: unknown;
  readonly scope: FieldScope | string | undefined;
  readonly validators: readonly ValidatorConfig[];
}

/**
 * Extension of a FieldInput for a Select field.
 */
export interface SelectInputFieldDeclaration
  extends InputFieldDeclaration, SelectInput {
  readonly items: readonly SelectInputItem[];
  readonly itemConditions?: { readonly [key: string]: ConditionDefinition };
  readonly hasTypeAhead?: boolean;
}

/**
 * Extension of a FieldInput for a Text field.
 */
export interface TextInputFieldDeclaration
  extends InputFieldDeclaration, TextInput {
  readonly autocapitalize?: string | undefined;
  readonly keyboardType?: string | undefined;
  readonly format?: string | undefined;
}

// ==================================================================
// Textbox Fields
// ==================================================================

/**
 * Field Textbox
 */
export interface TextboxFieldDeclaration extends TemplateDefinition {
  readonly text: InlineTranslateable;
  readonly assets: unknown[];
  readonly image: string | undefined;
}

// ==================================================================
// Action Fields
// ==================================================================

/**
 * Field Action
 */
export interface ActionFieldDeclaration extends ActionDefinition {
  readonly action: string;
  readonly actionConfig: unknown;
}

// ==================================================================
// Field Guards
// ==================================================================

export function isFieldDeclaration(
  value: FormDeclarationNode,
): value is FieldDeclaration {
  return (
    value instanceof FieldDeclaration ||
    ('widget' in value && typeof value['widget'] === 'string')
  );
}

export function isFormSelectField(
  field: FormDeclarationNode,
): field is FormSelectField {
  return (
    field &&
    isFieldDeclaration(field) &&
    isSelectInputField(field.widgetOptions)
  );
}

export function isFormInputField(
  field: FormDeclarationNode,
): field is FormInputField {
  return field && isFieldDeclaration(field) && isInputField(field.widgetType);
}

export function isFormTextboxField(
  field: FormDeclarationNode,
): field is FormTextboxField {
  return field && isFieldDeclaration(field) && isTextboxField(field.widgetType);
}

export function isFormActionField(
  field: FormDeclarationNode,
): field is FormActionField {
  return field && isFieldDeclaration(field) && isActionField(field.widgetType);
}

// ==================================================================
// Field Widget Guards
// ==================================================================

function isInputField(widget: WidgetType | undefined): widget is 'input' {
  return !!widget && widget === 'input';
}

function isSelectInputField(
  widget: FormFieldWidget | undefined,
): widget is SelectInputFieldDeclaration {
  return !!widget && 'items' in widget && Array.isArray(widget['items']);
}

function isTextboxField(widget: WidgetType | undefined): widget is 'textbox' {
  return !!widget && widget === 'textbox';
}

function isActionField(widget: WidgetType | undefined): widget is 'action' {
  return !!widget && widget === 'action';
}
